import React from 'react';
import BlogFigureControls from './BlogFigureControls';

const turns = [
  {
    label: 'CHECKPOINT',
    timing: 'AFTER EACH ACTION',
    detail: 'Node A checkpoints the sandbox to S3.'
  },
  {
    label: 'NODE A FAILS',
    timing: 'ON FAILURE',
    detail: 'Node A and its live sandbox fail.'
  },
  {
    label: 'RESTORE TO ANY NODE',
    timing: '2.9S P99',
    detail: 'S3 restores to Node B or Node C.'
  },
  {
    label: 'NODE B RESUMES',
    timing: 'NEXT ACTION',
    detail: 'Node B resumes the sandbox.'
  }
];

const StatelessReliabilityFigure: React.FC = () => {
  const [turn, setTurn] = React.useState(0);
  const [playing, setPlaying] = React.useState(
    () => typeof window === 'undefined' || !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const failed = turn >= 1;
  const restoring = turn === 2;
  const resumed = turn === 3;

  React.useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(
      () => setTurn(current => (current + 1) % turns.length),
      2200
    );
    return () => window.clearTimeout(timer);
  }, [playing, turn]);

  const previous = () => {
    setPlaying(false);
    setTurn(current => (current - 1 + turns.length) % turns.length);
  };

  const next = () => {
    setPlaying(false);
    setTurn(current => (current + 1) % turns.length);
  };

  return (
    <figure className="stateless-reliability">
      <div className="stateless-reliability__frame">
        <header className="stateless-reliability__header">
          <span>SANDBOX RELIABILITY</span>
          <strong>TURN {turn + 1} / {turns.length}</strong>
        </header>

        <svg
          className="stateless-reliability__diagram"
          viewBox="0 0 600 286"
          role="img"
          aria-label={turns[turn].detail}
        >
          <defs>
            <marker id="reliability-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
          </defs>

          {turn === 0 && (
            <path className="stateless-reliability__path is-active" d="M 96 154 C 108 98, 193 70, 246 70" />
          )}
          {restoring && (
            <>
              <path className="stateless-reliability__path is-active" d="M 300 100 C 300 125, 300 136, 300 150" />
              <path className="stateless-reliability__path is-active" d="M 354 70 C 411 77, 488 104, 504 150" />
            </>
          )}
          {resumed && (
            <path className="stateless-reliability__path is-active" d="M 300 100 C 300 125, 300 136, 300 150" />
          )}

          <g className="stateless-reliability__store">
            <rect x="250" y="38" width="100" height="62" rx="13" />
            <path d="M 250 56 C 274 69, 326 69, 350 56" />
            <text x="300" y="55">S3</text>
            <text className="is-status" x="300" y="84">{turn === 0 ? 'WRITING SNAPSHOT' : 'LATEST SNAPSHOT'}</text>
          </g>

          <g className={`stateless-reliability__node${failed ? ' is-failed' : ' is-primary'}`}>
            <rect className="node-shell" x="30" y="154" width="132" height="96" rx="8" />
            <text x="96" y="178">NODE A</text>
            <rect className="sandbox-chip" x="45" y="188" width="102" height="27" rx="4" />
            <text className="is-sandbox" x="96" y="206">{failed ? 'SANDBOX LOST' : 'SANDBOX'}</text>
            <text className="is-status" x="96" y="236">{failed ? 'FAILED' : 'PRIMARY'}</text>
            {failed && <path className="stateless-reliability__failure" d="m58 190 76 23m0-23-76 23" />}
          </g>

          <g className={`stateless-reliability__node${restoring ? ' is-ready' : ''}${resumed ? ' is-resumed' : ''}`}>
            <rect className="node-shell" x="234" y="154" width="132" height="96" rx="8" />
            <text x="300" y="178">NODE B</text>
            <rect className="sandbox-chip" x="249" y="188" width="102" height="27" rx="4" />
            <text className="is-sandbox" x="300" y="206">{resumed ? 'SANDBOX' : 'EMPTY'}</text>
            <text className="is-status" x="300" y="236">{restoring ? 'RESTORE TARGET' : resumed ? 'RESUMED' : 'AVAILABLE'}</text>
          </g>

          <g className={`stateless-reliability__node${restoring ? ' is-ready' : ''}`}>
            <rect className="node-shell" x="438" y="154" width="132" height="96" rx="8" />
            <text x="504" y="178">NODE C</text>
            <rect className="sandbox-chip" x="453" y="188" width="102" height="27" rx="4" />
            <text className="is-sandbox" x="504" y="206">EMPTY</text>
            <text className="is-status" x="504" y="236">{restoring ? 'RESTORE TARGET' : 'AVAILABLE'}</text>
          </g>
        </svg>

        <div
          className="stateless-reliability__state"
          aria-live={playing ? 'off' : 'polite'}
          aria-atomic="true"
        >
          <strong>{turns[turn].label}</strong>
          <time>{turns[turn].timing}</time>
          <span>{turns[turn].detail}</span>
        </div>

        <BlogFigureControls
          className="stateless-reliability__controls"
          playing={playing}
          onPrevious={previous}
          onToggle={() => setPlaying(current => !current)}
          onNext={next}
          label="Sandbox reliability playback controls"
          itemLabel="turn"
        />
      </div>
      <figcaption>
        The sandbox dies with Node A, survives as a snapshot in S3, and resumes on Node B.
      </figcaption>
    </figure>
  );
};

export default StatelessReliabilityFigure;
